
import { PermissionsState, DEFAULT_PERMISSIONS } from '@/types/permissions';

/**
 * Safe fallback permissions for offline/error scenarios
 */
export class FallbackPermissionsService {
  private static readonly SAFE_FALLBACK_PERMISSIONS: PermissionsState = {
    ...DEFAULT_PERMISSIONS,
    permissionsStatus: 'ready',
    lastChecked: Date.now(),
    source: 'fallback',
  };

  private static readonly READ_ONLY_PERMISSIONS: PermissionsState = {
    isAuthenticated: true,
    isPremium: false, // Conservative: no premium access in fallback
    isAdmin: false,   // Critical: never allow admin in fallback
    permissionsStatus: 'ready',
    lastChecked: Date.now(),
    source: 'fallback',
  };

  /**
   * Get safe fallback permissions based on last known state and error context
   */
  static getFallbackPermissions(
    lastKnownPermissions?: PermissionsState | null,
    userId?: string,
    _errorContext?: Record<string, unknown>
  ): PermissionsState {
    // If no user ID, return unauthenticated state
    if (!userId) {
      console.log('🔒 Fallback: No user ID, returning unauthenticated permissions');
      return this.SAFE_FALLBACK_PERMISSIONS;
    }

    // If we have valid last known permissions, create read-only version
    if (lastKnownPermissions && this.isValidLastKnownState(lastKnownPermissions)) {
      console.log('🔒 Fallback: Using read-only version of last known permissions');
      return {
        ...this.READ_ONLY_PERMISSIONS,
        isAuthenticated: true,
        // Allow premium features if previously verified and not expired
        isPremium: this.shouldAllowPremiumFallback(lastKnownPermissions),
        // Never allow admin in fallback - too risky
        isAdmin: false,
        lastChecked: lastKnownPermissions.lastChecked,
        source: 'fallback',
      };
    }

    // Conservative fallback for authenticated users
    console.log('🔒 Fallback: Using conservative authenticated permissions');
    return this.READ_ONLY_PERMISSIONS;
  }

  /**
   * Check if last known permissions are recent and valid enough for fallback
   */
  private static isValidLastKnownState(permissions: PermissionsState): boolean {
    const now = Date.now();
    const maxAge = 2 * 60 * 60 * 1000; // 2 hours max age for fallback trust
    
    return (
      permissions.permissionsStatus === 'ready' &&
      permissions.lastChecked > 0 &&
      (now - permissions.lastChecked) < maxAge &&
      permissions.isAuthenticated === true
    );
  }

  /**
   * Determine if premium features should be available in fallback mode
   */
  private static shouldAllowPremiumFallback(lastKnownPermissions: PermissionsState): boolean {
    // Only allow if previously verified as premium and recently checked
    const recentlyChecked = (Date.now() - lastKnownPermissions.lastChecked) < (30 * 60 * 1000); // 30 minutes
    return lastKnownPermissions.isPremium && recentlyChecked;
  }

  /**
   * Get permission capabilities in fallback mode
   */
  static getFallbackCapabilities() {
    return {
      canRead: true,
      canWrite: false, // No writes in fallback mode
      canAdmin: false, // Never allow admin actions
      canPremium: 'conditional', // Based on last known state
      explanation: 'Limited functionality available offline. Reconnect for full access.',
    };
  }

  /**
   * Check if a specific action is allowed in fallback mode
   */
  static isActionAllowedInFallback(
    action: 'read' | 'write' | 'admin' | 'premium',
    fallbackPermissions: PermissionsState
  ): boolean {
    switch (action) {
      case 'read':
        return fallbackPermissions.isAuthenticated;
      case 'write':
        return false; // No writes in fallback
      case 'admin':
        return false; // Never allow admin actions in fallback
      case 'premium':
        return fallbackPermissions.isPremium;
      default:
        return false;
    }
  }
}
